import { createRng } from "@/lib/seededRandom";
import type { Client } from "@/types";
import { ACCOUNT_MANAGERS, OPERATIONS_MANAGERS, TEAM_LEADERS, VIRTUAL_ASSISTANTS } from "./employees";
import { AVATAR_COLORS, INDUSTRIES, REGIONS, companyName } from "./constants";

const CLIENT_SEED = 42042;
export const CLIENT_COUNT = 620;

const AI_RECOMMENDATIONS = [
  "Schedule a QBR to surface expansion into a second VA seat.",
  "Escalate to OM — SLA compliance has slipped two consecutive months.",
  "Client sentiment trending down; recommend proactive check-in this week.",
  "Strong health & tenure — propose annual contract to lock in pricing.",
  "Open issue backlog growing; assign a second VA to clear queue.",
  "No recent contact in 30+ days — schedule a relationship check-in.",
  "Renewal window opening in 60 days — begin renewal conversation now.",
  "High satisfaction, flat usage — pitch an adjacent service line.",
  "Repeated onboarding friction — pair with senior TL for 2 weeks.",
  "Champion changed roles — re-establish executive sponsor relationship.",
];

function buildClients(): Client[] {
  const rng = createRng(CLIENT_SEED);
  const clients: Client[] = [];

  for (let i = 0; i < CLIENT_COUNT; i++) {
    const tenureMonths = rng.int(1, 84);
    const healthScore = rng.int(28, 99);
    const riskLevel =
      healthScore < 45 ? "critical" : healthScore < 62 ? "high" : healthScore < 80 ? "medium" : "low";
    const status: Client["status"] =
      healthScore < 35 && rng.bool(0.3)
        ? "at-risk"
        : tenureMonths < 2
          ? "onboarding"
          : rng.bool(0.015)
            ? "paused"
            : rng.bool(0.01)
              ? "churned"
              : "active";

    const mrr = rng.int(8, 220) * 100;
    const vaCount = rng.int(1, 3);
    const expansionOpportunity = healthScore > 72 && rng.bool(0.35);
    const totalOpenIssues = rng.pickWeighted<number>([
      [0, 40],
      [1, 25],
      [2, 15],
      [3, 10],
      [rng.int(4, 9), 10],
    ]);
    const openClientTickets = rng.int(0, totalOpenIssues);

    clients.push({
      id: `client-${i + 1}`,
      name: companyName(rng),
      avatarColor: rng.pick(AVATAR_COLORS),
      industry: rng.pick(INDUSTRIES),
      mrr,
      healthScore,
      riskLevel,
      sentiment: healthScore > 70 ? "positive" : healthScore > 45 ? "neutral" : "negative",
      status,
      assignedAM: rng.pick(ACCOUNT_MANAGERS).name,
      assignedOM: rng.pick(OPERATIONS_MANAGERS).name,
      assignedTL: rng.pick(TEAM_LEADERS).name,
      assignedVAs: rng.sample(VIRTUAL_ASSISTANTS, vaCount).map((v) => v.name),
      openIssues: totalOpenIssues,
      openClientTickets,
      openVATickets: totalOpenIssues - openClientTickets,
      lastContact: rng.daysAgo(45),
      nextFollowUp: rng.daysFromNow(30, -5),
      expansionOpportunity,
      expansionValue: expansionOpportunity ? rng.int(5, 60) * 100 : undefined,
      aiRecommendation: rng.pick(AI_RECOMMENDATIONS),
      satisfactionScore: Math.min(100, Math.max(20, healthScore + rng.int(-15, 10))),
      slaCompliance: Math.min(100, Math.max(50, healthScore + rng.int(-10, 15))),
      tenureMonths,
      contractValue: mrr * 12,
      region: rng.pick(REGIONS),
      employeeCount: rng.int(3, 250),
    });
  }

  return clients;
}

export const CLIENTS: Client[] = buildClients();

export function getClientById(id: string): Client | undefined {
  return CLIENTS.find((c) => c.id === id);
}
